import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Trash2, KeyRound, Eye, EyeOff, Search } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["admin", "director", "participant", "caregiver"];

function PasswordInput({ id, value, onChange, testId, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1">
      <input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} data-testid={testId}
        placeholder={placeholder} className="w-full h-10 px-3 pr-10 rounded-lg border border-[#d6cecf]" />
      <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testId}-toggle`}
        aria-label={show ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#545454] hover:text-[#920e12]">
        {show ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "participant" });
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, query, roleFilter]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success(`Created ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "participant" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}/role`, { role });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, role } : x)));
      toast.success(`${u.email} is now ${role}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((us) => us.filter((x) => x.id !== u.id));
      toast.success(`Deleted ${u.email}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submitReset = async () => {
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.put(`/admin/users/${resetTarget.id}/password`, { password: newPassword });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-users">
      <form onSubmit={createUser} className="bg-white rounded-2xl border border-[#dfd8da] p-6 mb-6" data-testid="create-user-form">
        <h2 className="text-lg font-bold text-[#1a1617] flex items-center gap-2"><UserPlus size={18} /> Add a user</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label htmlFor="u-name">Name</Label>
            <input id="u-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" />
          </div>
          <div>
            <Label htmlFor="u-email">Email</Label>
            <input id="u-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" />
          </div>
          <div>
            <Label htmlFor="u-pass">Password</Label>
            <PasswordInput id="u-pass" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="new-user-password" placeholder="Min 6 chars" />
          </div>
          <div>
            <Label htmlFor="u-role">Role</Label>
            <select id="u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf] bg-white capitalize">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <Button type="submit" className="h-10 bg-[#920e12] hover:bg-[#550001] text-white font-bold" data-testid="create-user-btn">Create user</Button>
        </div>
      </form>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#545454]" aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="user-search" placeholder="Search by name or email…"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#d6cecf]" aria-label="Search users" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="user-role-filter" className="h-10 px-3 rounded-lg border border-[#d6cecf] bg-white capitalize">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-[#dfd8da]" data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-bold text-[#1a1617]">{u.name}{u.is_self && <span className="ml-2 text-xs text-[#bf6163]">(you)</span>}</td>
                <td className="p-3 text-[#545454]">{u.email}</td>
                <td className="p-3">
                  <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`user-role-${u.id}`} className="h-9 px-2 rounded-md border border-[#d6cecf] bg-white capitalize">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setResetTarget(u); setNewPassword(""); }} data-testid={`reset-pass-${u.id}`} className="border-[#920e12] text-[#920e12]">
                      <KeyRound size={14} className="mr-1" /> Reset
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteUser(u)} disabled={u.is_self} data-testid={`delete-user-${u.id}`} className="border-[#991b1b] text-[#991b1b] disabled:opacity-30">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-6 text-[#545454]">No users match your search.</p>}
      </div>

      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Reset password{resetTarget ? ` — ${resetTarget.email}` : ""}</DialogTitle>
            <DialogDescription>Set a new password for this user. Share it with them securely; they can change it after logging in.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="reset-pass">New password</Label>
            <PasswordInput id="reset-pass" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} testId="reset-password-input" placeholder="At least 6 characters" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
            <Button onClick={submitReset} className="bg-[#920e12] text-white" data-testid="reset-password-submit">Set password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
